import { useState, useEffect } from 'react';
import classnames from 'classnames';
import { Heading, Text } from '@acb_devel/design-system';
import type { User } from '@services/users';
import { getUserYears } from '@utils/date';
import {
  getRegistrations,
  RegistrationStatus,
  Registration,
} from '@services/registrations';
import { fetchRecognitions, Recognition } from '@services/recognitions';
import { AchievementList } from '@components/shared/atoms/AchievementList/AchievementList';
import { AchievementListItem } from '@components/shared/atoms/AchievementListItem/AchivemenetListItem';

import styles from './AchievementsCard.module.scss';

export function AchievementsCard({
  user,
  className,
}: {
  user: User | undefined;
  className?: string;
}) {
  const [participations, setParticipations] = useState<Registration[]>([]);
  const [recognitions, setRecognitions] = useState<Recognition[]>([]);

  useEffect(() => {
    if (!user) {
      return;
    }

    fetchRecognitions({ userId: user.id }).then(setRecognitions);

    getRegistrations({
      ended: true,
      status: RegistrationStatus.CONFIRMED,
      userId: user.id,
    }).then((data) => {
      setParticipations(data.results);
    });
  }, [user]);

  if (!user?.profile) {
    return null;
  }

  return (
    <div className={classnames(styles.card, className)}>
      <div className={styles.card__header}>
        <div className={styles.card__avatarCointainer}>
          {user?.profile?.profilePictureFullUrl && (
            <img
              className={styles.card__avatar}
              src={user.profile.profilePictureFullUrl}
              alt="Avatar"
            />
          )}
          {!user?.profile?.profilePictureFullUrl && (
            <div className={styles.card__avatar} />
          )}
        </div>
        <div className={styles.card__userInfo}>
          <Heading as="h3" variant="display-4">
            {user.profile.firstName} {user.profile.lastName}
          </Heading>
          <Text variant="body-1">
            {new Date(user.profile.dateOfBirth).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}{' '}
            ({getUserYears(user.profile.dateOfBirth).toString()} años)
          </Text>
          <Text variant="body-1">
            {`${user.profile.city} (${user.profile.province})`}
          </Text>
        </div>
      </div>
      {(participations.length > 0 || recognitions.length > 0) && (
        <div className={styles.card__separator} />
      )}
      {participations.length > 0 && (
        <AchievementList title="Participaciones como voluntario">
          {participations.map((participation) => (
            <AchievementListItem
              key={participation.id}
              text={participation.event.fullName}
            />
          ))}
        </AchievementList>
      )}
      {recognitions.length > 0 && (
        <AchievementList title="Menciones">
          {recognitions.map((recognition) => (
            <AchievementListItem
              key={recognition.id}
              text={recognition.displayName}
            />
          ))}
        </AchievementList>
      )}
    </div>
  );
}
